import React, { useState, useEffect, useMemo } from 'react';
import { api } from '../api/client';
import { Resource, RESOURCE_TYPES, RESOURCE_FUNCTIONS } from '../types';
import { SidePanel } from '../components/common/SidePanel';
import { useT } from '../i18n';
import { Package } from 'lucide-react';

export function InventoryResourcesPage() {
  const { t } = useT();
  const [resources, setResources] = useState<Resource[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedResource, setSelectedResource] = useState<Resource | null>(null);

  useEffect(() => {
    api.getResources()
      .then((data: any) => setResources(data))
      .finally(() => setLoading(false));
  }, []);

  const resourceGrid = useMemo(() => {
    const grid: Record<string, Record<string, Resource[]>> = {};
    for (const fn of RESOURCE_FUNCTIONS) {
      grid[fn] = {};
      for (const type of RESOURCE_TYPES) {
        grid[fn][type] = [];
      }
    }
    for (const resource of resources) {
      const fn = RESOURCE_FUNCTIONS.find((f) => f === resource.function);
      if (fn && grid[fn][resource.type]) {
        grid[fn][resource.type].push(resource);
      }
    }
    return grid;
  }, [resources]);

  if (loading) {
    return (
      <div className="inventory-page">
        <div className="flex items-center justify-center" style={{ height: 200 }}>
          <div className="spinner" />
        </div>
      </div>
    );
  }

  if (resources.length === 0) {
    return (
      <div className="inventory-page">
        <div className="empty-state-card">
          <div className="empty-state">
            <div className="empty-state-icon"><Package /></div>
            <div className="empty-state-title">{t('inv.resources.emptyTitle')}</div>
            <div className="empty-state-desc">{t('inv.resources.emptyDesc')}</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="inventory-page">
      <div className="matrix-grid-container">
        <div
          className="matrix-grid"
          style={{ '--matrix-cols': `48px repeat(${RESOURCE_TYPES.length}, minmax(190px, 1fr))` } as React.CSSProperties}
        >
          <div className="matrix-grid-corner"></div>
          {RESOURCE_TYPES.map((type) => (
            <div key={type} className="matrix-grid-col">{type}</div>
          ))}

          {RESOURCE_FUNCTIONS.map((fn) => (
            <React.Fragment key={fn}>
              <div className="matrix-grid-row">{fn}</div>
              {RESOURCE_TYPES.map((type) => (
                <div key={`${fn}-${type}`} className="matrix-grid-cell matrix-grid-cell--wrap">
                  {resourceGrid[fn]?.[type]?.map((resource) => (
                    <div key={resource.id} className="inventory-card">
                      <span className="inventory-card-title">{resource.title}</span>
                      <button
                        className="inventory-card-info"
                        onClick={() => setSelectedResource(resource)}
                        title={t('inv.showDetails')}
                      >
                        i
                      </button>
                    </div>
                  ))}
                </div>
              ))}
            </React.Fragment>
          ))}
        </div>
      </div>

      <SidePanel
        open={!!selectedResource}
        onClose={() => setSelectedResource(null)}
        title={selectedResource?.title || ''}
      >
        {selectedResource && (
          <>
            <div className="panel-detail-section">
              <div className="panel-detail-label">{t('inv.label.type')}</div>
              <div className="panel-detail-value">
                <span className={`badge badge-${selectedResource.type.toLowerCase()}`}>
                  {selectedResource.type}
                </span>
              </div>
            </div>
            <div className="panel-detail-section">
              <div className="panel-detail-label">{t('inv.label.function')}</div>
              <div className="panel-detail-value">{selectedResource.function}</div>
            </div>
            <div className="panel-detail-section">
              <div className="panel-detail-label">{t('inv.label.description')}</div>
              <div className="panel-detail-value">
                {selectedResource.description || t('inv.noDescription')}
              </div>
            </div>
          </>
        )}
      </SidePanel>
    </div>
  );
}
